// StudentResources.jsx - Updated to use public endpoint
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';
const apiUrl = process.env.REACT_APP_API_URL;

function StudentResources() {
  const [books, setBooks] = useState([]);
  const [courses, setCourses] = useState([]); // filter options: [{ id, coursename }]
  const [selectedCourse, setSelectedCourse] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const booksPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('studentSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in.');
        setLoading(false);
        return;
      }
      const authHeader = { headers: { Authorization: `Bearer ${sessionData.token}` } };

      // Books for the student's courses + the courses the student joined (for the filter).
      const [booksRes, coursesRes] = await Promise.all([
        axios.get(`${apiUrl}/api/student/books`, authHeader),
        axios.get(`${apiUrl}/api/student/courses`, authHeader),
      ]);

      const fetchedBooks = booksRes.data.success ? booksRes.data.books : [];
      setBooks(fetchedBooks);

      // Filter options: courses the student joined, unioned with any course a
      // resource actually belongs to (so every listed PDF stays filterable).
      const courseMap = new Map();
      if (coursesRes.data.success) {
        (coursesRes.data.courses || []).forEach((c) => {
          courseMap.set(String(c.id), c.coursename);
        });
      }
      fetchedBooks.forEach((b) => {
        if (b.course_id != null && !courseMap.has(String(b.course_id))) {
          courseMap.set(String(b.course_id), b.coursename);
        }
      });
      const courseList = Array.from(courseMap, ([id, coursename]) => ({ id, coursename }));
      setCourses(courseList);

      // One course -> auto-select it. More than one -> default to ALL.
      setSelectedCourse(courseList.length === 1 ? courseList[0].id : 'ALL');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading resources.');
    } finally {
      setLoading(false);
    }
  };

const handleView = (book) => {
  let filename = '';
  if (book.pdf) {
    filename = book.pdf.split('/').pop();
  }

  if (!filename) {
    alert('Error: Could not extract filename from PDF path');
    return;
  }

  // Use the same endpoint that works for admin
  const pdfUrl = `${apiUrl}/api/public/pdf?file=${encodeURIComponent(filename)}`;

  navigate('/student-view-pdf', {
    state: {
      bookTitle: book.title,
      pdfUrl: pdfUrl,
      courseName: book.coursename,
      filename: filename
    }
  });
};

  const handleFilterChange = (value) => {
    setSelectedCourse(value);
    setCurrentPage(1);
  };

  const filteredBooks = selectedCourse === 'ALL'
    ? books
    : books.filter((book) => String(book.course_id) === String(selectedCourse));

  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredBooks.length / booksPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb mb-4">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/dashboard" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Home</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">Resources</span></li>
            </ul>
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-24">
              <h4 className="mb-16">My Resources</h4>
              {error && <div className="alert alert-danger mb-16">{error}</div>}
              {!loading && !error && courses.length > 0 && (
                <div className="d-flex align-items-center gap-2 flex-wrap" style={{ maxWidth: 400 }}>
                  <label htmlFor="courseFilter" className="h6 mb-0 text-gray-300 flex-shrink-0">
                    Filter by course:
                  </label>
                  <select
                    id="courseFilter"
                    className="form-control py-11"
                    value={selectedCourse}
                    onChange={(e) => handleFilterChange(e.target.value)}
                  >
                    {courses.length > 1 && <option value="ALL">All Courses</option>}
                    {courses.map((c) => (
                      <option key={c.id} value={c.id}>{c.coursename}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="card-body p-0 overflow-x-auto">
              {loading && <div className="alert alert-info mb-0" role="alert">Loading resources...</div>}
              {error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
              {!loading && !error && books.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No resources available.
                </div>
              )}
              {!loading && !error && books.length > 0 && filteredBooks.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No resources for the selected course.
                </div>
              )}
              {!loading && !error && filteredBooks.length > 0 && (
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th className="h6 text-gray-300">Title</th>
                      <th className="h6 text-gray-300">Course</th>
                      <th className="h6 text-gray-300">Created</th>
                      <th className="h6 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBooks.map((book) => (
                      <tr key={book.id}>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {book.title.length > 30 ? `${book.title.substring(0, 30)}...` : book.title}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {book.coursename.length > 30 ? `${book.coursename.substring(0, 30)}...` : book.coursename}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {new Date(book.created).toLocaleDateString()}
                          </span>
                        </td>
                        <td>
                          <button
                            onClick={() => handleView(book)}
                            className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer flex-between flex-wrap">
              <span className="text-gray-900">
                Showing {filteredBooks.length === 0 ? 0 : (currentPage - 1) * booksPerPage + 1} to {Math.min(currentPage * booksPerPage, filteredBooks.length)} of {filteredBooks.length} entries
              </span>
              <ul className="pagination flex-align flex-wrap">
                {pageNumbers.map(number => (
                  <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                    <a
                      onClick={() => setCurrentPage(number)}
                      className="page-link h-44 w-44 flex-center text-15 rounded-8 fw-medium"
                      href="#!"
                    >
                      {number}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
}

export default StudentResources;
